import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, BookOpen, ClipboardCheck, Database, FileText } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Checkbox } from "../../../ui/components/Checkbox";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import {
  approveGuidance,
  fetchGuidance,
  requestGuidanceChanges,
  saveGuidance,
  type GuidanceContent,
  type GuidanceDetail,
} from "../../lib/api/insights";
import { fetchDataCatalog, type DataCatalogKey } from "../../lib/api/products";
import { GuidanceStatusBadge } from "./GuidanceListPage";
import { GuidanceStringListEditor } from "./GuidanceStringListEditor";
import * as s from "./GuidanceEditorPage.css";

const SECTION_ALERT = "guidance-alert";
const SECTION_CONTENT = "guidance-content";
const SECTION_EVIDENCE = "guidance-evidence";
const SECTION_REVIEW = "guidance-review";

const EMPTY: GuidanceContent = { summary: "", likelyCauses: [], selfServiceSteps: [], contactLionIf: [], evidenceKeys: [] };

function clean(content: GuidanceContent): GuidanceContent {
  const list = (items: string[]) => items.map((v) => v.trim()).filter(Boolean);
  return {
    summary: content.summary.trim(),
    likelyCauses: list(content.likelyCauses),
    selfServiceSteps: list(content.selfServiceSteps),
    contactLionIf: list(content.contactLionIf),
    evidenceKeys: [...content.evidenceKeys].sort(),
  };
}

function sameContent(a: GuidanceContent, b: GuidanceContent): boolean {
  return JSON.stringify(clean(a)) === JSON.stringify(clean(b));
}

/** The template headline uses the summary's first sentence when no model call is made. */
function firstSentence(text: string): string {
  return text.trim().split(/(?<=[.!?])\s/)[0]!.slice(0, 200);
}

function errorMessage(err: unknown, fallback: string, conflict: string): string {
  if (err instanceof ProApiClientError && err.data?.code === "VERSION_CONFLICT") return conflict;
  return err instanceof Error && err.message ? err.message : fallback;
}

export function GuidanceEditorPage({ catalogAlertId }: { catalogAlertId: string }) {
  const { t } = useI18n();
  const { accessToken, user, userPayload } = useAuth();
  const navigate = useNavigate();
  const token = accessToken ?? localStorage.getItem("access_token");
  const currentUserId = user?.id ?? userPayload?.sub ?? null;
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("system-insights-guidance-editor");

  const [detail, setDetail] = React.useState<GuidanceDetail | null>(null);
  const [keys, setKeys] = React.useState<DataCatalogKey[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<GuidanceContent>(EMPTY);
  const [keyFilter, setKeyFilter] = React.useState("");
  const [copyFrom, setCopyFrom] = React.useState("");
  const [copyNotice, setCopyNotice] = React.useState<string | null>(null);
  const [reviewNote, setReviewNote] = React.useState("");
  const [busy, setBusy] = React.useState<null | "save" | "submit" | "approve" | "changes">(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoadError(null);
    try {
      const d = await fetchGuidance(token, catalogAlertId);
      setDetail(d);
      setForm(d.guidance ? { ...d.guidance } : EMPTY);
      const catalog = await fetchDataCatalog(token, d.alert.catalogId).catch(() => null);
      setKeys(
        (catalog?.keys ?? [])
          .filter((k) => k.isActive && (k.kind ?? "realtime") === "realtime" && k.valueType === "number")
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t("pro.system.insights.guidance.loadFailed"));
    }
  }, [token, catalogAlertId, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const guidance = detail?.guidance ?? null;
  const loaded = guidance ? { ...guidance } : EMPTY;
  const dirty = detail ? !sameContent(form, loaded) : false;
  const isAuthor = Boolean(guidance && currentUserId && guidance.authoredByUserId === currentUserId);
  const summaryMissing = !form.summary.trim();
  const copySources = (detail?.otherCatalogs ?? []).filter((o) => o.guidance);

  const run = async (kind: NonNullable<typeof busy>, action: () => Promise<unknown>, success: string) => {
    setBusy(kind);
    setActionError(null);
    setSaved(null);
    try {
      await action();
      setSaved(success);
      await load();
    } catch (err) {
      setActionError(
        errorMessage(err, t("pro.system.insights.guidance.saveFailed"), t("pro.system.insights.guidance.conflict")),
      );
    } finally {
      setBusy(null);
    }
  };

  const save = (submitForReview: boolean) =>
    run(
      submitForReview ? "submit" : "save",
      () =>
        saveGuidance(token!, catalogAlertId, clean(form), {
          submitForReview,
          expectedVersion: guidance?.version ?? null,
        }),
      submitForReview ? t("pro.system.insights.guidance.submitted") : t("pro.system.insights.guidance.savedDraft"),
    );

  const applyCopy = () => {
    const source = copySources.find((o) => o.catalogAlertId === copyFrom)?.guidance;
    if (!source) return;
    const available = new Set(keys.map((k) => k.key));
    const evidenceKeys = source.evidenceKeys.filter((k) => available.has(k));
    const dropped = source.evidenceKeys.filter((k) => !available.has(k));
    setForm({ ...source, evidenceKeys });
    setCopyNotice(
      dropped.length
        ? t("pro.system.insights.guidance.copiedDropped", { keys: dropped.join(", ") })
        : t("pro.system.insights.guidance.copied"),
    );
  };

  const toggleKey = (key: string, checked: boolean) =>
    setForm((f) => ({
      ...f,
      evidenceKeys: checked ? [...new Set([...f.evidenceKeys, key])] : f.evidenceKeys.filter((k) => k !== key),
    }));

  const filteredKeys = React.useMemo(() => {
    const q = keyFilter.trim().toLowerCase();
    const matches = q ? keys.filter((k) => k.key.toLowerCase().includes(q) || k.label.toLowerCase().includes(q)) : keys;
    // Selected keys stay visible even when they don't match the filter.
    const selected = keys.filter((k) => form.evidenceKeys.includes(k.key) && !matches.includes(k));
    return [...selected, ...matches];
  }, [keys, keyFilter, form.evidenceKeys]);

  const unknownKeys = form.evidenceKeys.filter((k) => !keys.some((x) => x.key === k));

  const sectionLayout = bindSectionLayout([
    { id: SECTION_ALERT, label: t("pro.system.insights.guidance.section.alert"), icon: <BookOpen size={14} /> },
    { id: SECTION_CONTENT, label: t("pro.system.insights.guidance.section.content"), icon: <FileText size={14} /> },
    { id: SECTION_EVIDENCE, label: t("pro.system.insights.guidance.section.evidence"), icon: <Database size={14} /> },
    { id: SECTION_REVIEW, label: t("pro.system.insights.guidance.section.review"), icon: <ClipboardCheck size={14} /> },
  ]);

  if (loadError) {
    return (
      <div style={{ padding: 24 }}>
        <Callout variant="error">{loadError}</Callout>
      </div>
    );
  }
  if (!detail) return <AuthTransitionScreen variant="fill" message={t("pro.system.insights.loading")} />;

  const { alert } = detail;
  const disabled = busy !== null;

  return (
    <DetailPageLayout
      header={{
        media: <BookOpen size={48} />,
        title: `${alert.code} · ${alert.name}`,
        subtitle: alert.catalogName ?? undefined,
      }}
      contextActions={[
        {
          id: "back",
          label: t("pro.system.insights.guidance.back"),
          icon: <ArrowLeft size={14} />,
          onClick: () => navigate({ to: "/system/insights/guidance" }),
        },
      ]}
      {...sectionLayout}
    >
      <CollapsibleSection
        id={SECTION_ALERT}
        title={t("pro.system.insights.guidance.section.alert")}
        icon={<BookOpen size={20} />}
        expanded={isExpanded(SECTION_ALERT)}
        onToggleExpanded={() => toggle(SECTION_ALERT)}
        statusIndicators={[<GuidanceStatusBadge key="status" status={guidance?.status ?? "none"} t={t} />]}
      >
        <div className={s.sectionInner}>
          <dl className={s.meta}>
            <dt className={s.metaTerm}>{t("pro.system.insights.guidance.col.catalog")}</dt>
            <dd className={s.metaValue}>{alert.catalogName ?? alert.catalogId}</dd>
            <dt className={s.metaTerm}>{t("pro.system.insights.guidance.col.type")}</dt>
            <dd className={s.metaValue}>
              {t("pro.system.insights.guidance.typeSeverity", { type: alert.type, severity: alert.severity })}
            </dd>
            <dt className={s.metaTerm}>{t("pro.system.insights.guidance.col.fired")}</dt>
            <dd className={s.metaValue}>{alert.incidentCount90d}</dd>
            <dt className={s.metaTerm}>{t("pro.system.insights.guidance.catalogDescription")}</dt>
            <dd className={s.metaValue}>{alert.description || "—"}</dd>
            {guidance ? (
              <>
                <dt className={s.metaTerm}>{t("pro.system.insights.guidance.version")}</dt>
                <dd className={s.metaValue}>
                  {t("pro.system.insights.guidance.versionLine", {
                    version: guidance.version,
                    name: guidance.authoredByName ?? "—",
                    when: new Date(guidance.updatedAt).toLocaleString(),
                  })}
                </dd>
              </>
            ) : null}
            {guidance?.status === "approved" ? (
              <>
                <dt className={s.metaTerm}>{t("pro.system.insights.guidance.approvedBy")}</dt>
                <dd className={s.metaValue}>
                  {guidance.approvedByName ?? "—"}
                  {guidance.approvedAt ? ` · ${new Date(guidance.approvedAt).toLocaleString()}` : ""}
                </dd>
              </>
            ) : null}
          </dl>
          {alert.type === "fault" ? <Callout variant="info">{t("pro.system.insights.guidance.faultRule")}</Callout> : null}
          {guidance?.status === "approved" ? (
            <Callout variant="info">{t("pro.system.insights.guidance.editingApproved")}</Callout>
          ) : guidance && guidance.liveVersion !== null ? (
            <Callout variant="info">
              {t("pro.system.insights.guidance.pendingOverLive", { live: guidance.liveVersion, version: guidance.version })}
            </Callout>
          ) : null}
          {guidance?.reviewNote ? (
            <Callout variant="error">
              {t("pro.system.insights.guidance.changesRequested", {
                name: guidance.reviewedByName ?? "—",
                note: guidance.reviewNote,
              })}
            </Callout>
          ) : null}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_CONTENT}
        title={t("pro.system.insights.guidance.section.content")}
        icon={<FileText size={20} />}
        expanded={isExpanded(SECTION_CONTENT)}
        onToggleExpanded={() => toggle(SECTION_CONTENT)}
      >
        <div className={s.sectionInner}>
          {copySources.length > 0 ? (
            <div className={s.field}>
              <label className={s.label} htmlFor="guidance-copy-from">
                {t("pro.system.insights.guidance.copyFrom")}
              </label>
              <div className={s.copyRow}>
                <select
                  id="guidance-copy-from"
                  className={s.select}
                  value={copyFrom}
                  disabled={disabled}
                  onChange={(e) => setCopyFrom(e.target.value)}
                >
                  <option value="">{t("pro.system.insights.guidance.copyChoose")}</option>
                  {copySources.map((o) => (
                    <option key={o.catalogAlertId} value={o.catalogAlertId}>
                      {o.catalogName} ({t(`pro.system.insights.guidance.status.${o.guidance!.status}`)})
                    </option>
                  ))}
                </select>
                <Button type="button" size="sm" color="neutral" variant="outline" disabled={disabled || !copyFrom} onClick={applyCopy}>
                  {t("pro.system.insights.guidance.copyApply")}
                </Button>
              </div>
              {copyNotice ? <p className={s.hint}>{copyNotice}</p> : null}
            </div>
          ) : null}

          <div className={s.field}>
            <label className={s.label} htmlFor="guidance-summary">
              {t("pro.system.insights.guidance.summary")}
            </label>
            <p className={s.hint}>{t("pro.system.insights.guidance.summaryHint")}</p>
            <textarea
              id="guidance-summary"
              className={s.textarea}
              value={form.summary}
              maxLength={2000}
              disabled={disabled}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
            />
            {form.summary.trim() ? (
              <div className={s.preview}>
                <span className={s.previewTitle}>{t("pro.system.insights.guidance.previewLabel")}</span>
                <strong>{alert.name}</strong> — {firstSentence(form.summary)}
              </div>
            ) : null}
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="guidance-causes">
              {t("pro.system.insights.guidance.likelyCauses")}
            </label>
            <p className={s.hint}>{t("pro.system.insights.guidance.likelyCausesHint")}</p>
            <GuidanceStringListEditor
              id="guidance-causes"
              items={form.likelyCauses}
              disabled={disabled}
              onChange={(likelyCauses) => setForm((f) => ({ ...f, likelyCauses }))}
            />
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="guidance-steps">
              {t("pro.system.insights.guidance.steps")}
            </label>
            <p className={s.hint}>{t("pro.system.insights.guidance.stepsHint")}</p>
            <GuidanceStringListEditor
              id="guidance-steps"
              items={form.selfServiceSteps}
              disabled={disabled}
              onChange={(selfServiceSteps) => setForm((f) => ({ ...f, selfServiceSteps }))}
            />
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="guidance-contact">
              {t("pro.system.insights.guidance.contactLionIf")}
            </label>
            <p className={s.hint}>{t("pro.system.insights.guidance.contactLionIfHint")}</p>
            <GuidanceStringListEditor
              id="guidance-contact"
              items={form.contactLionIf}
              disabled={disabled}
              onChange={(contactLionIf) => setForm((f) => ({ ...f, contactLionIf }))}
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_EVIDENCE}
        title={t("pro.system.insights.guidance.section.evidence")}
        subtitle={t("pro.system.insights.guidance.keysSelected", { count: form.evidenceKeys.length })}
        icon={<Database size={20} />}
        expanded={isExpanded(SECTION_EVIDENCE)}
        onToggleExpanded={() => toggle(SECTION_EVIDENCE)}
      >
        <div className={s.sectionInner}>
          <p className={s.hint}>{t("pro.system.insights.guidance.evidenceHint")}</p>
          {keys.length === 0 ? (
            <p className={s.hint}>{t("pro.system.insights.guidance.noKeys")}</p>
          ) : (
            <>
              <input
                type="search"
                className={s.keyFilter}
                value={keyFilter}
                placeholder={t("pro.system.insights.guidance.keyFilter")}
                aria-label={t("pro.system.insights.guidance.keyFilter")}
                onChange={(e) => setKeyFilter(e.target.value)}
              />
              <div className={s.keyGrid}>
                {filteredKeys.map((k) => (
                  <label key={k.key} className={s.keyOption}>
                    <Checkbox
                      checked={form.evidenceKeys.includes(k.key)}
                      disabled={disabled || (!form.evidenceKeys.includes(k.key) && form.evidenceKeys.length >= 30)}
                      onCheckedChange={(c) => toggleKey(k.key, c)}
                      aria-label={k.label}
                    />
                    <span>
                      {k.label}
                      {k.canonicalUnit ? ` (${k.canonicalUnit})` : ""} <span className={s.keyCode}>{k.key}</span>
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
          {unknownKeys.length > 0 ? (
            <p className={s.fieldError}>{t("pro.system.insights.guidance.unknownKeys", { keys: unknownKeys.join(", ") })}</p>
          ) : null}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_REVIEW}
        title={t("pro.system.insights.guidance.section.review")}
        icon={<ClipboardCheck size={20} />}
        expanded={isExpanded(SECTION_REVIEW)}
        onToggleExpanded={() => toggle(SECTION_REVIEW)}
      >
        <div className={s.sectionInner}>
          {summaryMissing ? <p className={s.fieldError}>{t("pro.system.insights.guidance.summaryRequired")}</p> : null}
          {actionError ? <Callout variant="error">{actionError}</Callout> : null}
          {saved && !actionError ? <Callout variant="success">{saved}</Callout> : null}

          <div className={s.actions}>
            <Button
              type="button"
              color="neutral"
              variant="outline"
              size="md"
              disabled={disabled || summaryMissing || (!dirty && Boolean(guidance))}
              loading={busy === "save"}
              onClick={() => void save(false)}
            >
              {t("pro.system.insights.guidance.saveDraft")}
            </Button>
            <Button
              type="button"
              color="primary"
              variant="solid"
              size="md"
              disabled={disabled || summaryMissing || (!dirty && guidance?.status === "in_review")}
              loading={busy === "submit"}
              onClick={() => void save(true)}
            >
              {t("pro.system.insights.guidance.submit")}
            </Button>
          </div>

          {guidance?.status === "in_review" ? (
            isAuthor ? (
              <Callout variant="info">{t("pro.system.insights.guidance.awaitingOther")}</Callout>
            ) : dirty ? (
              <Callout variant="info">{t("pro.system.insights.guidance.reviewDirty")}</Callout>
            ) : (
              <div className={s.field}>
                <label className={s.label} htmlFor="guidance-review-note">
                  {t("pro.system.insights.guidance.reviewNote")}
                </label>
                <textarea
                  id="guidance-review-note"
                  className={s.textarea}
                  value={reviewNote}
                  maxLength={2000}
                  disabled={disabled}
                  placeholder={t("pro.system.insights.guidance.reviewNotePlaceholder")}
                  onChange={(e) => setReviewNote(e.target.value)}
                />
                <div className={s.actions}>
                  <Button
                    type="button"
                    color="neutral"
                    variant="outline"
                    size="md"
                    disabled={disabled || !reviewNote.trim()}
                    loading={busy === "changes"}
                    onClick={() =>
                      void run(
                        "changes",
                        () => requestGuidanceChanges(token!, catalogAlertId, guidance.version, reviewNote.trim()),
                        t("pro.system.insights.guidance.changesSent"),
                      ).then(() => setReviewNote(""))
                    }
                  >
                    {t("pro.system.insights.guidance.requestChanges")}
                  </Button>
                  <Button
                    type="button"
                    color="primary"
                    variant="solid"
                    size="md"
                    disabled={disabled}
                    loading={busy === "approve"}
                    onClick={() =>
                      void run(
                        "approve",
                        () => approveGuidance(token!, catalogAlertId, guidance.version),
                        t("pro.system.insights.guidance.approved"),
                      )
                    }
                  >
                    {t("pro.system.insights.guidance.approve")}
                  </Button>
                </div>
              </div>
            )
          ) : null}
        </div>
      </CollapsibleSection>
    </DetailPageLayout>
  );
}
